import styled from 'styled-components';
import type { JSX } from 'react';
import { Card } from '@shared/ui/Card';
import {
  IconBanknote,
  IconBarChart,
  IconCalculator,
  IconClipboardCheck,
  IconClipboardList,
  IconClock,
  IconFileMinus,
  IconFileStack,
  IconIdCard,
  IconPackage,
  IconPickaxe,
  IconRuler,
  IconTarget,
  IconTruck,
  IconUsers,
  IconUtensils,
} from '@shared/ui/icons';
import type { EconDocIcon } from '@entities/economic/types';

/** SVG-иконка типа документа по семантическому id из entities. Эмодзи в
 *  `<option>` заменить нельзя (нативный select не рендерит разметку), поэтому
 *  опции — чистый текст, а иконка ВЫБРАННОГО типа показывается рядом с select. */
export const DOC_ICONS: Record<EconDocIcon, (props: { size?: number }) => JSX.Element> = {
  act: IconClipboardCheck,
  'file-stack': IconFileStack,
  'bar-chart': IconBarChart,
  clock: IconClock,
  truck: IconTruck,
  target: IconTarget,
  banknote: IconBanknote,
  users: IconUsers,
  'id-card': IconIdCard,
  pickaxe: IconPickaxe,
  ruler: IconRuler,
  package: IconPackage,
  'file-minus': IconFileMinus,
  utensils: IconUtensils,
  'clipboard-list': IconClipboardList,
  calculator: IconCalculator,
};

/** Месяцы для селекта отчётного периода. `<input type="month">` не
 *  поддерживается Firefox (рендерится текстовым полем — свободный ввод ломал
 *  создание обработки 400-кой), поэтому период выбирается двумя селектами. */
export const MONTHS = [
  ['01', 'Январь'],
  ['02', 'Февраль'],
  ['03', 'Март'],
  ['04', 'Апрель'],
  ['05', 'Май'],
  ['06', 'Июнь'],
  ['07', 'Июль'],
  ['08', 'Август'],
  ['09', 'Сентябрь'],
  ['10', 'Октябрь'],
  ['11', 'Ноябрь'],
  ['12', 'Декабрь'],
] as const;

/** Годы для селекта периода: от 2023 до следующего года, новые сверху. */
export const YEARS = (() => {
  const current = new Date().getFullYear();
  const list: string[] = [];
  for (let y = current + 1; y >= 2023; y -= 1) list.push(String(y));
  return list;
})();

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const FormTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 15px;
  font-weight: 600;
`;

export const FileHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

/** Подсказка вместо зоны загрузки, когда права на запись нет: молча
 *  недоступная зона выглядит как поломка. */
export const ReadOnlyHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(4)} 0`};
`;

export const FileList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const FileItem = styled.li`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
`;

export const FileName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  & > svg {
    vertical-align: -2px;
    margin-right: ${({ theme }) => theme.spacing(1.5)};
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const FileSize = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const RemoveBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

export const TypeHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Отчётный период двумя селектами (месяц + год) — кросс-браузерно. */
export const PeriodRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};

  & > select:first-child {
    flex: 1.4;
  }
  & > select:last-child {
    flex: 1;
  }
`;

/** Селект типа с SVG-иконкой выбранного типа слева. */
export const TypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  & > select {
    flex: 1;
    min-width: 0;
  }
`;

export const TypeIconBox = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const PiiNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & > svg {
    flex-shrink: 0;
  }
`;

export const DupNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const UnsetNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

export const ListHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const RunCard = styled(Card)`
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const RunTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const RunPeriod = styled.div`
  font-weight: 650;
  font-size: 15px;
`;

export const RunMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const OpenHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-left: auto;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }
  ${RunCard}:hover & > svg {
    transform: translateX(3px);
  }
`;

export const EmptyState = styled(Card)`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
  padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(4)};
`;
